import { useState, useCallback, useRef, ReactNode } from "react"
import styled from "styled-components"
import Search from "@mui/icons-material/Search"
import ContentCopy from "@mui/icons-material/ContentCopy"
import Close from "@mui/icons-material/Close"
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined"
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown"
import { toast } from "react-toastify"
import { rgba } from "polished"
import colors from "~constants/colors"
import ProofOfOwnershipCollapsibleList from "~components/proofOfOwnership/ProofOfOwnershipCollapsibleList"
import ValidationButton from "~components/proofOfOwnership/ValidationButton"
import {
  proofOfOwnershipGlobalFilter,
  proofOfOwnershipPipelineFilters,
} from "./proofOfOwnershipFilters"
import { ProofOfOwnership } from "~shapes/proofOfOwnership"
import { POO_ASSIGNED_VIEW, PooView } from "~utils/pooAssignment"

export const SORT_OPTIONS = [
  { value: "created_at,asc", label: "Oldest" },
  { value: "created_at,desc", label: "Newest" },
]

interface Props {
  isFetchingPoo: boolean
  isErrorFetchingPooList: boolean
  selectedStatement: ProofOfOwnership
  setSelectedStatement: (poo: ProofOfOwnership) => void
  pooList: ProofOfOwnership[]
  onSearch: (el: { email: string }) => void
  searchEmail: string | null
  pooFilterStatus: PooView | null
  setPooFilterStatus: (status: PooView | null) => void
  pooCounts?: Record<string, number>
  isFetchingPooCounts?: boolean
  // Size of the assigned queue, shown on the "My validations" pill (the counts
  // endpoint has no per-assignee bucket, so this is the loaded list length).
  assignedCount?: number
  // Whether the own assigned queue has more than the loaded first page, so the
  // pill can show "<count>+" instead of an under-counted exact number.
  assignedHasMore?: boolean
  sort: string
  setSort: (sort: string) => void
  startValidation: () => void
  onLoadMore?: () => void
  hasMore?: boolean
  isLoadingMore?: boolean
  // Admin override: take over a POO claimed by another operator.
  canTakeOver?: boolean
  onTakeOver?: () => void
  // Version of the AI analyzer behind the current suggestion, shown discreetly
  // next to the validation badge. Absent until a suggestion is loaded.
  analyzerVersion?: string
  // Extra content rendered immediately to the right of the version badge.
  versionExtra?: ReactNode
}

const ProofOfOwnershipHeader = ({
  isFetchingPoo,
  isErrorFetchingPooList,
  selectedStatement,
  setSelectedStatement,
  pooList,
  onSearch,
  searchEmail,
  pooFilterStatus,
  setPooFilterStatus,
  pooCounts,
  isFetchingPooCounts,
  assignedCount,
  assignedHasMore,
  sort,
  setSort,
  startValidation,
  onLoadMore,
  hasMore,
  isLoadingMore,
  canTakeOver,
  onTakeOver,
  analyzerVersion,
  versionExtra,
}: Props) => {
  const [searchValue, setSearchValue] = useState(searchEmail || "")
  // The POO list dropdown is opened from the active pill/tab (no separate
  // button). It anchors under whichever control is active.
  const [listOpen, setListOpen] = useState(false)
  const pipelineGroupRef = useRef<HTMLDivElement>(null)
  const globalGroupRef = useRef<HTMLDivElement>(null)

  const isAssignedView = pooFilterStatus === POO_ASSIGNED_VIEW

  const handleSearchSubmit = useCallback(() => {
    onSearch({ email: searchValue })
  }, [searchValue, onSearch])

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") handleSearchSubmit()
    },
    [handleSearchSubmit],
  )

  const handleClearSearch = useCallback(() => {
    setSearchValue("")
    onSearch({ email: "" })
  }, [onSearch])

  const copyId = useCallback(async () => {
    await navigator.clipboard.writeText(selectedStatement.id)
    toast.info("Copied to clipboard", {
      hideProgressBar: true,
      autoClose: 1000,
      theme: "dark",
    })
  }, [selectedStatement.id])

  const globalFilter = proofOfOwnershipGlobalFilter
  const isGlobalActive = pooFilterStatus === globalFilter.value
  const globalCount = pooCounts?.[globalFilter.value.toLowerCase()]
  const hasSelection = !isErrorFetchingPooList && !!selectedStatement.id

  return (
    <HeaderBar>
      {/* ── Primary zone (left): pipeline pills (also open the list) ── */}
      {/* Personal pipeline: pick up from Created, claim into My validations.
          Clicking the active pill toggles its list of POOs (anchored below). */}
      <PipelineGroup ref={pipelineGroupRef}>
        <FilterGroup>
          {proofOfOwnershipPipelineFilters.map(item => {
            const isActive = pooFilterStatus === item.value
            const isAssignedPill = item.value === POO_ASSIGNED_VIEW
            const count = isAssignedPill
              ? assignedCount
              : pooCounts?.[item.value.toLowerCase()]
            const isLoadingCount =
              !isAssignedPill && isFetchingPooCounts && count === undefined
            // The assigned queue has no count endpoint, so its badge shows the
            // loaded first-page size with a trailing "+" when more pages exist.
            const countLabel = isLoadingCount
              ? "…"
              : isAssignedPill && assignedHasMore
                ? `${count ?? 0}+`
                : (count ?? 0)
            return (
              <FilterPill
                key={item.value}
                $isActive={isActive}
                onClick={() => {
                  if (isActive) {
                    setListOpen(open => !open)
                  } else {
                    setPooFilterStatus(item.value as PooView)
                    setListOpen(false)
                  }
                }}
              >
                {item.text}
                <PillCount $isActive={isActive}>{countLabel}</PillCount>
                {isActive && (
                  <Chevron $open={listOpen} as={KeyboardArrowDown} />
                )}
              </FilterPill>
            )
          })}
        </FilterGroup>

        <ProofOfOwnershipCollapsibleList
          isOpen={listOpen && !isGlobalActive}
          onClose={() => setListOpen(false)}
          containerRef={pipelineGroupRef}
          setStatement={setSelectedStatement}
          selectedStatement={selectedStatement}
          list={isErrorFetchingPooList ? [] : pooList}
          isLoading={isFetchingPoo}
          onLoadMore={onLoadMore}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          showAssignee={!isAssignedView}
        />
      </PipelineGroup>

      {/* Primary action, kept right next to the selection (pills + list) so the
          pick → validate loop is a minimal mouse move. */}
      {hasSelection && (
        <ValidationButton
          item={selectedStatement}
          changeStatus={startValidation}
        />
      )}

      {/* Which analyzer build produced the current suggestion — kept tiny and
          muted so it's there for debugging without competing with the controls. */}
      {analyzerVersion && (
        <AnalyzerVersion title="AI analyzer version">
          {analyzerVersion}
        </AnalyzerVersion>
      )}
      {analyzerVersion && versionExtra}

      {/* Pushes the secondary controls to the right edge. */}
      <HeaderSpacer />

      {/* ── Secondary zone (right): occasional tools + oversight + actions ── */}
      {/* Search is occasional (find a specific user), so it lives out of the
          primary pick → validate path. */}
      {!isAssignedView && (
        <SearchField>
          <SearchIconStyled />
          <SearchInput
            type="text"
            placeholder="Search by email..."
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
          {searchValue && (
            <ClearBtn onClick={handleClearSearch}>
              <Close style={{ fontSize: 16 }} />
            </ClearBtn>
          )}
        </SearchField>
      )}

      {hasSelection && (
        <CopyIdButton
          onClick={copyId}
          title={`Copy ID #${selectedStatement.id.slice(-8)}`}
        >
          <ContentCopy style={{ fontSize: 16 }} />
        </CopyIdButton>
      )}

      <GlobalGroup ref={globalGroupRef}>
        <GlobalTab
          $isActive={isGlobalActive}
          onClick={() => {
            if (isGlobalActive) {
              setListOpen(open => !open)
            } else {
              // Oversight view: switching to it opens the list right away, since
              // scanning everything in validation is the whole point.
              setPooFilterStatus(globalFilter.value as PooView)
              setListOpen(true)
            }
          }}
          title="Everything currently being validated, by anyone"
        >
          <VisibilityOutlined style={{ fontSize: 16 }} />
          {globalFilter.text}
          <PillCount $isActive={isGlobalActive}>
            {isFetchingPooCounts && globalCount === undefined
              ? "…"
              : (globalCount ?? 0)}
          </PillCount>
          {isGlobalActive && (
            <Chevron $open={listOpen} as={KeyboardArrowDown} />
          )}
        </GlobalTab>

        <ProofOfOwnershipCollapsibleList
          isOpen={listOpen && isGlobalActive}
          onClose={() => setListOpen(false)}
          containerRef={globalGroupRef}
          align="right"
          setStatement={setSelectedStatement}
          selectedStatement={selectedStatement}
          list={isErrorFetchingPooList ? [] : pooList}
          isLoading={isFetchingPoo}
          onLoadMore={onLoadMore}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          showAssignee={!isAssignedView}
        />
      </GlobalGroup>

      {hasSelection && canTakeOver && onTakeOver && (
        <TakeOverButton onClick={onTakeOver}>Take over</TakeOverButton>
      )}

      <SortSelect value={sort} onChange={e => setSort(e.target.value)}>
        {SORT_OPTIONS.map(opt => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </SortSelect>
    </HeaderBar>
  )
}

const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  /* Match the 12px horizontal padding of the content below (PDF / form) so the
     left edge of the list button lines up with the document panel. */
  padding: 8px 12px 12px;
  flex-wrap: nowrap;
`

// Separates the primary zone (pipeline + find tools) from the secondary zone
// (sort + oversight) and the right-aligned primary action.
const HeaderSpacer = styled.div`
  flex: 1 1 auto;
  min-width: 16px;
`

// Wraps the pills and anchors the list dropdown right below them.
const PipelineGroup = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex-shrink: 0;
`

// Small raised "notification bubble" count on a pill — visible but takes almost
// no horizontal space (sits up-right of the label rather than beside it).
const PillCount = styled.span<{ $isActive: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
  min-width: 15px;
  height: 15px;
  margin-top: -2px;
  margin-left: 1px;
  padding: 0 4px;
  border-radius: 8px;
  font-size: 9px;
  font-weight: 700;
  line-height: 1;
  color: ${props => (props.$isActive ? colors.white : colors.gray6)};
  background: ${props =>
    props.$isActive ? colors.blue1 : rgba(colors.gray6, 0.15)};
`

// Caret on the active pill hinting it opens the list; rotates when open.
const Chevron = styled.span<{ $open: boolean }>`
  font-size: 16px !important;
  margin-right: -2px;
  transition: transform 0.15s ease;
  transform: ${props => (props.$open ? "rotate(180deg)" : "none")};
`

const FilterGroup = styled.div`
  display: flex;
  background: ${colors.gray1};
  border-radius: 8px;
  padding: 3px;
  gap: 2px;
  flex-shrink: 0;
`

const FilterPill = styled.button<{ $isActive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  background: ${props => (props.$isActive ? colors.white : "transparent")};
  color: ${props => (props.$isActive ? colors.blue1 : colors.gray6)};
  box-shadow: ${props =>
    props.$isActive ? "0 1px 3px rgba(0,0,0,0.08)" : "none"};

  &:hover {
    color: ${props => (props.$isActive ? colors.blue1 : colors.gray3)};
    background: ${props =>
      props.$isActive ? colors.white : rgba(colors.white, 0.6)};
  }
`

// Wraps the global tab and anchors its list dropdown below it (right-aligned).
const GlobalGroup = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex-shrink: 0;
`

// The org-wide oversight tab: a quiet, low-emphasis control (it's the least-used
// view) set apart from the primary pipeline. Lights up only when it's active.
const GlobalTab = styled.button<{ $isActive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid
    ${props => (props.$isActive ? rgba(colors.blue1, 0.3) : "transparent")};
  background: ${props =>
    props.$isActive ? rgba(colors.blue1, 0.08) : "transparent"};
  color: ${props => (props.$isActive ? colors.blue1 : colors.gray6)};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.15s ease;

  &:hover {
    color: ${colors.blue1};
    background: ${props =>
      props.$isActive ? rgba(colors.blue1, 0.08) : rgba(colors.blue1, 0.04)};
  }
`

// Quiet version tag for the AI analyzer build — small, muted, and tabular so it
// stays out of the way while remaining legible.
const AnalyzerVersion = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${colors.gray4};
  letter-spacing: 0.02em;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  flex-shrink: 0;
  cursor: default;
`

const TakeOverButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border: none;
  border-radius: 8px;
  background: ${colors.blue1};
  font-size: 13px;
  font-weight: 600;
  color: ${colors.white};
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
  transition:
    filter 0.12s ease,
    box-shadow 0.12s ease;

  &:hover {
    filter: brightness(0.93);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.16);
  }

  &:active {
    filter: brightness(0.88);
  }
`

const SearchField = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid ${colors.gray2};
  border-radius: 8px;
  padding: 0 12px;
  height: 40px;
  flex: 0 1 220px;
  min-width: 180px;
  background: ${colors.white};
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;

  &:focus-within {
    border-color: ${colors.blue1};
    box-shadow: 0 0 0 3px ${rgba(colors.blue1, 0.08)};
  }
`

const SearchIconStyled = styled(Search)`
  color: ${colors.gray4};
  font-size: 20px !important;
  flex-shrink: 0;
`

const SearchInput = styled.input`
  border: none;
  outline: none;
  font-size: 13px;
  color: ${colors.gray3};
  flex: 1;
  min-width: 0;
  background: transparent;

  &::placeholder {
    color: ${colors.gray4};
  }
`

const ClearBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  color: ${colors.gray4};
  padding: 2px;
  border-radius: 4px;
  flex-shrink: 0;

  &:hover {
    color: ${colors.gray3};
    background: ${colors.gray1};
  }
`

// Compact icon-only copy-ID control — copies the full id, shows the short id in
// the tooltip, and takes minimal space (it's an occasional action).
const CopyIdButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid ${colors.gray2};
  border-radius: 8px;
  background: ${colors.white};
  color: ${colors.gray6};
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.12s ease;

  &:hover {
    border-color: ${colors.blue1};
    color: ${colors.blue1};
    background: ${rgba(colors.blue1, 0.04)};
  }
`

const SortSelect = styled.select`
  height: 40px;
  padding: 0 10px;
  border: 1px solid ${colors.gray2};
  border-radius: 8px;
  background: ${colors.white};
  font-size: 13px;
  color: ${colors.gray3};
  cursor: pointer;
  flex-shrink: 0;
  outline: none;

  &:focus {
    border-color: ${colors.blue1};
    box-shadow: 0 0 0 3px ${rgba(colors.blue1, 0.08)};
  }
`

export default ProofOfOwnershipHeader
